import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import { CustomFieldSkeletons } from './CustomFieldSkeleton';
import Card from 'components/Card';
import Disclosure from 'components/Disclosure';
import { useSequenceCustomFieldDefinitions } from 'queries/sequences/customfields/useSequenceCustomFieldDefinitions';
import Button from 'components/Buttons/Button';
import { useCreateSequenceCustomFieldDefinition } from 'queries/sequences/customfields/useCreateSequenceCustomFieldDefinition';
import { CustomFieldDefinitionWithTargets } from 'types/customField';
import { v4 as uuid } from 'uuid';
import { CustomFieldDefinitionComponent } from './CustomField';
import { useUpdateSequenceCustomFieldDefinition } from 'queries/sequences/customfields/useUpdateSequenceCustomFieldDefinition';
import { useDeleteSequenceCustomFieldDefinition } from 'queries/sequences/customfields/useDeleteSequenceCustomFieldDefinition';
import { useUpdateUserSequenceCustomFieldDefinition } from 'queries/sequences/customfields/useUpdateUserSequenceCustomFieldDefinition';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useReorderSequenceCustomFieldDefinitions } from 'queries/sequences/customfields/useReorderSequenceCustomFieldDefinitions';
import swap from 'utils/arrays/swap';

function Body() {
  const { t } = useTranslation(['userSettings']);
  const { data: customFieldDefinitions = [], isLoading } =
    useSequenceCustomFieldDefinitions();
  const { mutateAsync: updateCustomFields } =
    useUpdateSequenceCustomFieldDefinition();
  const { mutateAsync: deleteCustomField } =
    useDeleteSequenceCustomFieldDefinition();
  const { mutateAsync: updateUserSequenceCustomField } =
    useUpdateUserSequenceCustomFieldDefinition();
  const { mutateAsync: reorderCustomFields } =
    useReorderSequenceCustomFieldDefinitions();

  const handleReorder = (index: number, diff: -1 | 1) => {
    const newOrderIds = swap(
      customFieldDefinitions.map(f => f.id),
      index,
      diff,
    );

    reorderCustomFields(newOrderIds);
  };

  if (isLoading) {
    return <CustomFieldSkeletons />;
  }

  if (customFieldDefinitions.length === 0) {
    return (
      <Card tw="mb-3 relative">
        <div tw="text-muted text-sm">
          {t('userSettings:customFields.noCustomFieldForSequences')}
        </div>
      </Card>
    );
  }

  return (
    <div tw="flex flex-col gap-y-4">
      {customFieldDefinitions.map((def, index) => (
        <Card tw="mb-3 relative" key={def.id}>
          <CustomFieldDefinitionComponent
            item={def}
            onChange={updateCustomFields}
            onDelete={() => deleteCustomField(def.id)}
            availableTypes={['text', 'select', 'multiselect']} // TODO : enable more types after migration
            availableTargets={['sequence', 'fdp', 'fdpSummary', 'fdpPhases']}
            definitionTargets={def.targets}
            onTargetToggle={target =>
              updateUserSequenceCustomField({
                customFieldId: def.id,
                body: {
                  targets: def.targets.includes(target)
                    ? def.targets.filter(v => v !== target)
                    : [...def.targets, target],
                },
              })
            }
            isFirstItem={index === 0}
            isLastItem={index === customFieldDefinitions.length - 1}
            onReorder={diff => handleReorder(index, diff)}
          />
        </Card>
      ))}
    </div>
  );
}

export function SequenceCustomFields() {
  const { t } = useTranslation(['userSettings']);
  const { mutateAsync: createField } = useCreateSequenceCustomFieldDefinition();
  return (
    <Disclosure fill>
      <Disclosure.Button
        minimal
        fill
        large
        iconPosition="left"
        alignText="left"
        iconRotation="bottom-to-top"
        tw="mb-3"
      >
        {t('userSettings:customFields.sequenceFicheDePrepPhase')}
      </Disclosure.Button>
      <Disclosure.Panel>
        <Body />
        <div tw="text-right">
          <Button
            type="button"
            tw="text-right"
            intent="primary"
            onClick={() => {
              const newCF: CustomFieldDefinitionWithTargets = {
                id: uuid(),
                name: '',
                pluralName: '',
                type: 'text',
                options: [],
                targets: ['fdp', 'fdpPhases', 'fdpSummary', 'sequence'],
              };
              return createField(newCF);
            }}
            small
            icon={PlusIcon}
          >
            {t('userSettings:customFields.addCustomFieldForSequences')}
          </Button>
        </div>
      </Disclosure.Panel>
    </Disclosure>
  );
}
